// One organization in the platform console (PLAT-11, D-168, D-182), in tabs: its health first (CompanyHealth.tsx),
// then its plan (Coach, Autopilot and AI minutes), its name, industry, time zone and country, its owners, its review
// and suspension, and what the platform has done to it. The add-ons decide what its phones ask for (APP-26); phones
// learn of a change at their next sync.
import { type FormEvent, useEffect, useState } from "react";
import { Link, NavLink, useParams } from "react-router";
import { useSession } from "../app/session";
import { Card, ErrorNote, Field, Loading, Page, useAction, useLoad, useToast } from "../app/ui";
import { BUSINESS_TYPES, type BusinessType } from "../lib/aiInstructions";
import { fmtDateTime, fmtNumber } from "../lib/format";
import { TIMEZONES } from "../lib/settings";
import type { AddOns, PlatformAuditRow, PlatformOrg as Org } from "../lib/types";
import {
  AddOnBadges,
  AddOnChoices,
  industryName,
  parseLimit,
  PeopleAllowedField,
  PlatformShell,
  TZ,
  waiting,
} from "./Platform";
import { CompanyHealthTab } from "./CompanyHealth";

/** The company page's tabs; Health is its own address, the others follow it. */
const TABS = [
  { key: "", label: "Health" },
  { key: "plan", label: "Plan" },
  { key: "company", label: "Company" },
  { key: "owners", label: "Owners" },
  { key: "status", label: "Status" },
  { key: "history", label: "History" },
];

export function PlatformOrg() {
  const { id, tab = "" } = useParams();
  const { api } = useSession();
  const orgs = useLoad(() => api.platformOrgs(), [api]);
  const org = orgs.data?.find((o) => o.id === id);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  const reload = async () => {
    await orgs.reload();
  };

  return (
    <PlatformShell>
      <ErrorNote error={orgs.error} />
      {orgs.loading && !orgs.data && <Loading />}
      {orgs.data && !org && (
        <Page title="No such organization" actions={<Link className="button" to="/customers/all">All customers</Link>}>
          <p className="faint">It may have been removed, or the link is wrong.</p>
        </Page>
      )}
      {org && (
        <Page
          title={org.name}
          sub={
            <span className="row">
              <AddOnBadges addOns={org.features} memberLimit={org.member_limit} />
              <span>{industryName(org.vertical)}</span>
              <span className="faint">
                · {count(org.active_members, "person", "people")} ({count(org.callers, "caller")}) ·{" "}
                {count(org.devices, "phone")} · since {fmtDateTime(org.created_at, TZ)}
              </span>
            </span>
          }
          actions={<Link className="button" to="/customers/all">All customers</Link>}
        >
          <nav className="page-tabs" aria-label="Company">
            {TABS.map((t) => (
              <NavLink
                key={t.key}
                to={`/org/${org.id}${t.key ? `/${t.key}` : ""}`}
                className={t.key === tab ? "active" : ""}
                end
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="stack">
            <Review org={org} onDone={reload} />
            {tab === "" && <CompanyHealthTab orgId={org.id} />}
            {tab === "plan" && (
              <Plan
                key={`plan-${JSON.stringify(org.features)}-${org.ai_minutes_included}-${org.member_limit}`}
                org={org}
                onDone={reload}
              />
            )}
            {tab === "company" && (
              <Details
                key={`details-${org.name}-${org.vertical}-${org.timezone}-${org.default_country}`}
                org={org}
                onDone={reload}
              />
            )}
            {tab === "owners" && <Owners org={org} onDone={reload} />}
            {tab === "status" && <Status key={`status-${org.status}`} org={org} onDone={reload} />}
            {tab === "history" && <History orgId={org.id} />}
          </div>
        </Page>
      )}
    </PlatformShell>
  );
}

/** "1 phone", "4 phones". */
function count(n: number, one: string, many = `${one}s`): string {
  return `${fmtNumber(n)} ${n === 1 ? one : many}`;
}

/** A company that signed itself up: one phone number and no AI until approved (PLAT-09). */
function Review({ org, onDone }: { org: Org; onDone: () => Promise<void> }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  if (org.reviewed_at !== null) return null;
  const approve = async () => {
    if (await run(() => api.platformApproveOrg(org.id), "Approved")) await onDone();
  };
  return (
    <Card title="Under review" actions={<button className="primary" disabled={busy} onClick={approve}>Approve</button>}>
      <p>
        It signed itself up, so it can invite one phone number and its calls are not analysed. Approving lifts both: it
        can invite its whole team, and its calls are analysed within its {fmtNumber(org.ai_minutes_included)}{" "}
        AI minutes a month. The {waiting(org.held_recordings)} go to the AI now. Choose its add-ons below first.
      </p>
    </Card>
  );
}

/** Coach, Autopilot and the AI minutes, changed with one reason (PLAT-06, PLAT-11). */
function Plan({ org, onDone }: { org: Org; onDone: () => Promise<void> }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const toast = useToast();
  const [addOns, setAddOns] = useState<AddOns>(org.features);
  const [minutes, setMinutes] = useState(String(org.ai_minutes_included));
  const [limit, setLimit] = useState(org.member_limit === null ? "" : String(org.member_limit));
  const [reason, setReason] = useState("");
  const addOnsChanged = addOns.coach !== org.features.coach || addOns.autopilot !== org.features.autopilot;
  const minutesChanged = Number(minutes) !== org.ai_minutes_included;
  const limitChanged = parseLimit(limit) !== org.member_limit;
  const changed = addOnsChanged || minutesChanged || limitChanged;
  // Its own setting keeps recordings without an add-on (D-170): that is what records_calls says with neither on.
  const keepsRecordings = org.records_calls && !org.features.coach && !org.features.autopilot;
  const hadAddOn = org.features.coach || org.features.autopilot;
  const hasAddOn = addOns.coach || addOns.autopilot;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    let queued = 0;
    const ok = await run(async () => {
      if (addOnsChanged) queued = await api.platformSetAddOns(org.id, addOns, reason.trim());
      if (minutesChanged) await api.platformSetMinutes(org.id, Number(minutes), reason.trim());
      if (limitChanged) await api.platformSetMemberLimit(org.id, parseLimit(limit), reason.trim());
    }, "Saved");
    if (ok) {
      if (queued > 0) toast(`${waiting(queued)} went to the AI`);
      await onDone();
    }
  };

  return (
    <Card title="Plan">
      <form onSubmit={submit}>
        <AddOnChoices value={addOns} onChange={setAddOns} keepsRecordings={keepsRecordings} />
        {addOnsChanged && (
          <p className="faint">
            Its phones learn of this when they next sync: when the app opens, and every 15 minutes.{" "}
            {hadAddOn && !hasAddOn && !keepsRecordings &&
              "They stop asking for the recordings folder and stop sending recordings; a recording already on its way " +
                "waits, unanalysed, until an add-on is on again."}
            {hadAddOn && !hasAddOn && keepsRecordings &&
              "It keeps its recordings, so they still arrive, and from now on no AI hears them."}
            {!hadAddOn && hasAddOn &&
              "Each phone then asks for the recordings folder, and sends its calls' recordings from then on."}
          </p>
        )}
        <div className="grid cols-3">
          <PeopleAllowedField value={limit} onChange={setLimit} />
          <Field label="AI minutes included per month" hint={`${fmtNumber(org.ai_minutes_used)} used this month.`}>
            <input
              type="number"
              min={0}
              max={1000000}
              required
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </Field>
          <Field label="Reason" hint="Kept in the audit log, where its owner can read it.">
            <input
              required={changed}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </div>
        <div className="actions">
          <button className="primary" disabled={busy || !changed}>
            Save plan
          </button>
        </div>
      </form>
    </Card>
  );
}

/** Its name, industry, time zone and country (PLAT-11). */
function Details({ org, onDone }: { org: Org; onDone: () => Promise<void> }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const [name, setName] = useState(org.name);
  const [vertical, setVertical] = useState<BusinessType>(org.vertical);
  const [timezone, setTimezone] = useState(org.timezone);
  const [country, setCountry] = useState(org.default_country);
  const changed = name.trim() !== org.name || vertical !== org.vertical || timezone !== org.timezone ||
    country.trim().toUpperCase() !== org.default_country;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await run(
      () => api.platformUpdateOrg(org.id, { name: name.trim(), vertical, timezone, defaultCountry: country.trim() }),
      "Saved",
    );
    if (ok) await onDone();
  };

  return (
    <Card title="Company">
      <form onSubmit={submit}>
        <div className="grid cols-2">
          <Field label="Name">
            <input required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field
            label="Industry"
            hint={vertical !== org.vertical
              ? "Changes its objection categories and the AI's wording, as the owner's own choice does. " +
                "Its stages and lead sources stay."
              : undefined}
          >
            <select value={vertical} onChange={(e) => setVertical(e.target.value as BusinessType)}>
              {BUSINESS_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </Field>
          <Field label="Time zone" hint="Its working hours, reminders and reports follow it.">
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {[...new Set([timezone, ...TIMEZONES])].map((z) => <option key={z}>{z}</option>)}
            </select>
          </Field>
          <Field label="Country" hint="Two letters, such as IN: how its phone numbers are read.">
            <input
              required
              maxLength={2}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={{ textTransform: "uppercase" }}
            />
          </Field>
        </div>
        <div className="actions">
          <button className="primary" disabled={busy || !changed}>Save company</button>
        </div>
      </form>
    </Card>
  );
}

/** Its owners, and inviting another (PLAT-05). */
function Owners({ org, onDone }: { org: Org; onDone: () => Promise<void> }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const [owner, setOwner] = useState("");
  const [contact, setContact] = useState("");
  const owners = (org.owners as { name: string; email: string | null; phone: string | null }[]) ?? [];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const who = contact.includes("@") ? { ownerEmail: contact.trim() } : { ownerPhone: contact.trim() };
    const ok = await run(
      () => api.platformInviteOwner({ orgId: org.id, ownerName: owner.trim(), ...who }),
      "Owner invited",
    );
    if (ok) {
      setOwner("");
      setContact("");
      await onDone();
    }
  };

  return (
    <Card title="Owners">
      <div className="list" style={{ marginBottom: 14 }}>
        {owners.map((w) => (
          <div className="list-item" key={w.name}>
            {w.name} <span className="faint">{w.email ?? w.phone}</span>
          </div>
        ))}
        {owners.length === 0 && <div className="list-item faint">No owner has signed in yet.</div>}
        {org.pending_invitations > 0 && (
          <div className="list-item faint">
            {org.pending_invitations} {org.pending_invitations === 1 ? "invitation" : "invitations"} not yet accepted
          </div>
        )}
      </div>
      <form onSubmit={submit} className="grid cols-3">
        <Field label="Owner's name">
          <input required value={owner} onChange={(e) => setOwner(e.target.value)} />
        </Field>
        <Field label="Owner's email or mobile" hint="They become an owner when they first sign in with it.">
          <input required value={contact} onChange={(e) => setContact(e.target.value)} />
        </Field>
        <div className="field">
          <span>&nbsp;</span>
          <button disabled={busy} style={{ alignSelf: "flex-start" }}>Invite owner</button>
        </div>
      </form>
    </Card>
  );
}

/** Suspend or reactivate, with a reason its people see (PLAT-05). */
function Status({ org, onDone }: { org: Org; onDone: () => Promise<void> }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const [reason, setReason] = useState("");
  const active = org.status === "active";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await run(
      () => api.platformSetStatus(org.id, active ? "suspended" : "active", reason.trim()),
      active ? "Suspended" : "Reactivated",
    );
    if (ok) await onDone();
  };

  return (
    <Card title="Status">
      <p className={active ? "faint" : undefined}>
        {active
          ? "Active. While suspended its people see nothing and no calls are processed. Nothing is deleted."
          : `Suspended: ${org.suspended_reason}`}
      </p>
      <form onSubmit={submit} className="grid cols-3">
        <Field label="Reason" hint="Its people see this reason.">
          <input required value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="field">
          <span>&nbsp;</span>
          <button className={active ? "danger solid" : "primary"} disabled={busy} style={{ alignSelf: "flex-start" }}>
            {active ? "Suspend" : "Reactivate"}
          </button>
        </div>
      </form>
    </Card>
  );
}

const ACTION_WORDS: Record<string, string> = {
  "organization.created": "Set up",
  "organization.approved": "Approved",
  "organization.features": "Add-ons changed",
  "organization.details": "Company details changed",
  "organization.ai_minutes": "AI minutes changed",
  "organization.suspended": "Suspended",
  "organization.active": "Reactivated",
  "invitation.created": "Owner invited",
  "owner.invited": "Owner invited",
  "organization.flags": "What we know changed",
  "organization.team_viewed": "Team looked at",
};

/** What the platform has done to it, newest first; loaded each time the tab opens, so a look at its team just now is
 * in it. */
function History({ orgId }: { orgId: string }) {
  const { api } = useSession();
  const audit = useLoad(() => api.platformAudit(), [api]);
  const rows: PlatformAuditRow[] = (audit.data ?? []).filter((a) => a.org_id === orgId);
  return (
    <Card title="Platform actions">
      <ErrorNote error={audit.error} />
      {audit.loading && !audit.data && <Loading />}
      <div className="list">
        {rows.map((a, i) => {
          const detail = a.detail && typeof a.detail === "object" && !Array.isArray(a.detail)
            ? a.detail as Record<string, unknown>
            : {};
          return (
            <div className="list-item row between" key={i}>
              <span>
                <strong>{ACTION_WORDS[a.action] ?? a.action.replace(/\./g, " ")}</strong>
                {typeof detail.reason === "string" && <span>: {detail.reason}</span>}{" "}
                <span className="faint">{a.actor_email}</span>
              </span>
              <span className="faint">{fmtDateTime(a.at, TZ)}</span>
            </div>
          );
        })}
        {audit.data && rows.length === 0 && <div className="list-item faint">Nothing yet.</div>}
      </div>
    </Card>
  );
}
